import { useMemo, type RefObject } from 'react';
import GanttHeader from './GanttHeader';
import GanttRow from './GanttRow';
import type { FlatNode, TimeUnit } from './timelineUtils';
import {
  calculateTimeRange,
  generateColumns,
  getTodayOffset,
  getTotalWidth,
  CELL_WIDTH,
  getPixelsPerDay,
  diffDays,
  ROW_HEIGHT,
} from './timelineUtils';

interface DropTarget {
  issueId: string;
  valid: boolean;
}

interface Props {
  flattenedNodes: FlatNode[];
  timeUnit: TimeUnit;
  onIssueClick: (issueId: string) => void;
  onScroll: () => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  onDateChange: (issueId: string, field: 'startDate' | 'dueDate', newDate: string) => void;
  onMoveStart: (issueId: string, issueType: string, e: React.MouseEvent) => void;
  dropTarget: DropTarget | null;
}

export default function GanttChart({
  flattenedNodes,
  timeUnit,
  onIssueClick,
  onScroll,
  scrollRef,
  onDateChange,
  onMoveStart,
  dropTarget,
}: Props) {
  const timeRange = useMemo(
    () => calculateTimeRange(flattenedNodes, timeUnit),
    [flattenedNodes, timeUnit],
  );
  const columns = useMemo(
    () => generateColumns(timeRange, timeUnit),
    [timeRange, timeUnit],
  );
  const totalWidth = getTotalWidth(columns, timeUnit);
  const todayOffset = getTodayOffset(timeRange, timeUnit);

  return (
    <div ref={scrollRef as React.RefObject<HTMLDivElement>} className="flex-1 overflow-auto" onScroll={onScroll}>
      <div style={{ minWidth: `${totalWidth}px` }}>
        <GanttHeader columns={columns} timeUnit={timeUnit} />
        <div className="relative">
          {/* Rows */}
          {flattenedNodes.map((node) => (
            <GanttRow
              key={node.issueId}
              node={node}
              timeRange={timeRange}
              timeUnit={timeUnit}
              totalWidth={totalWidth}
              onIssueClick={onIssueClick}
              onDateChange={onDateChange}
              onMoveStart={onMoveStart}
              dropTarget={dropTarget}
            />
          ))}
          {flattenedNodes.length === 0 && (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
              표시할 작업이 없습니다.
            </div>
          )}

          {/* 그리드 오버레이: 셀 경계선 + 주말 빗살무늬 */}
          <div className="absolute inset-0 pointer-events-none z-[1]">
            {renderGridOverlay(columns, timeRange, timeUnit)}
          </div>

          {/* Today line */}
          {todayOffset > 0 && (
            <div
              className="absolute top-0 bottom-0 w-0 border-l-2 border-dashed border-red-400 z-10 pointer-events-none"
              style={{ left: `${todayOffset}px` }}
            />
          )}
        </div>
        {/* IssueTree 하단 [+ 작업 추가] sticky footer와 높이를 맞추기 위한 빈 공간.
            없으면 좌측 스크롤이 우측보다 footer 높이만큼 더 스크롤되어 마지막 항목이 footer에 가려짐. */}
        <div style={{ height: `${ROW_HEIGHT}px` }} aria-hidden />
      </div>
    </div>
  );
}

const WEEKEND_HATCH =
  'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(0,0,0,0.07) 3px, rgba(0,0,0,0.07) 4px)';

/** 셀 경계선 + 주말 빗살무늬 오버레이 */
function renderGridOverlay(
  columns: import('./timelineUtils').TimeColumn[],
  timeRange: import('./timelineUtils').TimeRange,
  timeUnit: TimeUnit,
) {
  const cellW = CELL_WIDTH[timeUnit];
  const pxPerDay = getPixelsPerDay(timeUnit);
  const elements: JSX.Element[] = [];

  // 1) 셀 경계선 (모든 컬럼)
  columns.forEach((_, i) => {
    elements.push(
      <div
        key={`grid-${i}`}
        className="absolute top-0 bottom-0 border-r border-gray-200/60"
        style={{ left: i * cellW, width: cellW }}
      />,
    );
  });

  // 2) 주말 빗살무늬 (일 단위로 순회)
  const totalDays = diffDays(timeRange.maxDate, timeRange.minDate);
  const current = new Date(timeRange.minDate);
  for (let i = 0; i <= totalDays; i++) {
    const dow = current.getDay();
    if (dow === 0 || dow === 6) {
      elements.push(
        <div
          key={`wknd-${i}`}
          className="absolute top-0 bottom-0"
          style={{
            left: i * pxPerDay,
            width: pxPerDay,
            background: WEEKEND_HATCH,
          }}
        />,
      );
    }
    current.setDate(current.getDate() + 1);
  }

  return <>{elements}</>;
}
